import React, { useState } from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import TableGridFormField from './TableGridFormField';
import { StyledTableRow, StyledTableRowItem } from './TableGridFormik.styled';
import ConfirmationAction from 'components/ConfirmationAction/ConfirmationAction';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import { History, Save} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { flatten } from "lodash";
import { roles } from "commons/auth/roles";
import { useAppDispatch, useAppSelector } from "commons/store/hooks";
import { selectUserProfile } from "core/redux/userSlice";
import getInitialValues from 'features/ecrf/utils/getInitialValues';
import { useTranslation } from "react-i18next";
import Permission from "core/helpers/Permission";
import { deleteElementDataCollection, updateElementDataCollection } from "features/ecrf/redux/elementDataCollectionSlice";

const TableGridRow: React.FC<any> = (props: any) => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const userProfile = useAppSelector(selectUserProfile);
    const [isSending, setSending] = useState(false);
    const [isRemoving, setRemoving] = useState(false);
    const { t } = useTranslation();

    // const formInitialValues = getInitialValues(props.row?.elements);
    const initialValues = getInitialValues(props.columns, props.row?.elements || [], true);

    /**
     * This function is used to save actual row
     * @param values
     * @param actions
     */
    const handleSaveRow = async (values: typeof initialValues, actions: FormikHelpers<typeof initialValues>) => {
        setSending(true);

        try {
            const data = props.row?.elements?.map((el: any) => {
                return {
                    id: `/api/element_datas/${el.id}`,
                    data: flatten([values[el.element.id]])
                }
            });

            await dispatch(updateElementDataCollection({ id: props.row?.id, params: { elements: [...data] } }));
            await reloadData();

            enqueueSnackbar(`${t('saved')} ${new Date().toLocaleString('pl-PL')}`, { variant: 'info' });
        } catch (e: any) {
            enqueueSnackbar(e?.detail || e?.message, { variant: 'error' });
        }

        setSending(false);
    };

    /**
     * This function is used to remove actual row after confirmation
     * @param id
     */
    const removeRow = async (id: string) => {
        setRemoving(true);

        try {
          await dispatch(deleteElementDataCollection(id))
          await reloadData();

          enqueueSnackbar(t('record-deleted'), { variant: 'info' });
        } catch (error: any) {
          enqueueSnackbar(error?.detail || error?.message, { variant: 'warning' });
        }

        setRemoving(false);
    };

    /**
     * Reload data element
     */
    const reloadData = async () => {
        await props.fetchData(props.dataId);
        await props.changeReloadingValue();
    };

    return (
        <Box>
            <Formik initialValues={initialValues} onSubmit={handleSaveRow}>
                {(form) => <div role="form">
                    <StyledTableRow size={props.columns.length}>
                        {props.columns
                            .map((element: any, index: any) => {
                                const data = props.row?.elements.find(
                                    (d: any, i: any) =>
                                        (element?.id === d.element?.id && index === d.element?.position) ||
                                        index === d.element?.position
                                        || index === i
                                        // element?.position === d.element?.position
                                );

                                return (<StyledTableRowItem key={element.id}>
                                    <TableGridFormField
                                        style={{ width: '100%' }}
                                        rowId={props.row?.id}
                                        data={data?.data}
                                        options={data?.element?.options || element?.options}
                                        type={data?.element?.type || element?.type}
                                        name={data?.element?.id || element?.id || ''}
                                        label={data?.element?.label || element?.label}
                                        required={data?.element?.required || element?.required}
                                        dataId={data?.id}
                                        status={(!Permission.hasAccess(props?.permissionOperations, 'HISTORY') && props.status) || data?.mode === 'block'}
                                        disabled={data?.mode === 'block' || isSending}
                                        reloadData={reloadData.bind(this)}
                                    />
                                </StyledTableRowItem>)
                            })}

                        <Box ml={3} display={'flex'} alignItems={'center'}>
                            {(props.options?.saveRow) && (
                                <Tooltip title={t('save-record')} arrow>
                                    <span>
                                        <Button
                                            variant={'outlined'}
                                            color={'primary'}
                                            disabled={isSending || props.status}
                                            onClick={form.submitForm}
                                        >
                                            {isSending ? <CircularProgress size={24} /> : <Save/>}
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {
                              (userProfile.roles.some(
                                (role: string) => roles.admin.includes(role) || roles.monitor.includes(role)
                              ) || Permission.hasAccess(props?.permissionOperations, 'HISTORY'))
                              && (
                                <Tooltip title={t('history-record')} arrow>
                                    <span>
                                        <Button
                                            variant={'outlined'}
                                            color={'primary'}
                                            disabled={(!Permission.hasAccess(props?.permissionOperations, 'HISTORY') && props.status)}
                                            onClick={() => props.handleHistory(props.row.id)}
                                        >
                                            <History />
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {(props.options?.removeRow || Permission.hasAccess(props?.permissionOperations, 'DELETE')) && (
                                <ConfirmationAction
                                    confirmationTitle={t('are-you-sure-you-want-to-delete-this-record')}
                                    onClick={() => removeRow(props.row.id)}
                                    isProcessing={isRemoving}
                                >
                                    {({ handleClick }) => (
                                        <Tooltip title={t('remove-record')} arrow>
                                            <span>
                                                <Button
                                                    onClick={handleClick}
                                                    variant={'outlined'}
                                                    color={'primary'}
                                                    disabled={isRemoving || (Permission.hasAccess(props?.permissionOperations, 'DELETE') && props.status)}>
                                                    <DeleteSweepRoundedIcon />
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    )}
                                </ConfirmationAction>
                            )}
                        </Box>
                    </StyledTableRow>
                </div>
                }
            </Formik>
        </Box>
    );
};

export default TableGridRow;
